import {
  type AccountServices,
  type AccountAssociationContact,
  type AssociationType,
} from '@/portal-api.definition';
import { Check } from '@nosinovacao/fe-icons';
import { createContext, type ReactNode, useContext, useState } from 'react';

type ServicesContentPage =
  | 'main'
  | 'apps'
  | 'add-services'
  | 'holder-input'
  | 'contact-selection'
  | 'code-input'
  | 'success';

export interface FlowData {
  associationType: AssociationType;
  contactId: number | undefined;
  contactSelectionContacts: AccountAssociationContact[];
  selectedContact: string;
  code: string;
  services?: AccountServices;
}

interface ServicesContextValue {
  flow: ServicesContentPage;
  flowData: FlowData;
  setFlow: (flow: ServicesContentPage, data?: FlowData) => void;
}

const ServicesContext = createContext<ServicesContextValue | null>(null);

export function useServicesContext() {
  const context = useContext(ServicesContext);
  if (!context) {
    throw new Error('useServicesContext must be used within a ServicesContextProvider');
  }
  return context;
}

export function ServicesContextProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [flow, setFlow] = useState<ServicesContentPage>('main');
  const [flowData, setFlowData] = useState<FlowData>({} as unknown as FlowData);
  function handleSetFlow(newFlow: ServicesContentPage, data?: FlowData) {
    setFlow(newFlow);
    setFlowData(data ?? ({} as FlowData));
  }
  const value = { flow, setFlow: handleSetFlow, flowData };
  return <ServicesContext.Provider value={value}>{children}</ServicesContext.Provider>;
}

export function ServiceFlowPage({
  flow,
  children,
}: Readonly<{
  flow: ServicesContentPage;
  children: ReactNode;
}>) {
  const { flow: activeFlow } = useServicesContext();
  if (flow !== activeFlow) return null;
  return <>{children}</>;
}

export function backLocation(flow: ServicesContentPage, data: FlowData): ServicesContentPage {
  switch (flow) {
    case 'holder-input':
    case 'contact-selection':
      return 'add-services';
    case 'code-input':
      if (data.associationType === 'Nif' || data.associationType === 'Passport') {
        return 'contact-selection';
      }
      return 'add-services';
    default:
      return 'main';
  }
}

export function hasBack(flow: ServicesContentPage): boolean {
  return ['apps', 'holder-input', 'contact-selection', 'code-input'].includes(flow);
}

export function getIconForFlow(flow: ServicesContentPage) {
  if (flow === 'success') return <Check width={48} height={48} className="text-nos-green" />;
}
